import type { UIComponent } from '@gik/core/types/UI';
import bemBlock, { useBemCN } from '@gik/core/utils/bemBlock';
import type {
  AnimatedAttachImageResult,
  ConvertType,
  GIFImage,
  RasterAttachImageResult,
  RasterImage,
  SVGImage,
  VectorAttachImageResult,
} from '@gik/core/utils/FileUtils';
import FileUtils, { AcceptType, DefaultMaxImageAttachmentSize } from '@gik/core/utils/FileUtils';
import ImageUtils from '@gik/core/utils/ImageUtils';
import noop from '@gik/core/utils/noop';
import { Button } from '@gik/ui/Button';
import { UI } from '@gik/ui/UIManager';
import PhotographIcon from '@heroicons/react/outline/PhotographIcon';
import React from 'react';
import type { IImageCropData } from '../ImageCropper';
import { ImageCropper } from '../ImageCropper';
import { SvgIcon } from '../SvgIcon';

type FileResult = {
  file: File;
};

type ImageResult = {
  raster?: RasterImage;
  vector?: SVGImage;
  gif?: GIFImage;
};

export type ImagePickerResult = FileResult & ImageResult;

interface IImagePickerHookProps {
  maxSize?: number;
  dialogTitle?: string;
  aspect?: number;
  onChange?(result: ImagePickerResult): void;
  acceptType?: AcceptType[];
  convertTo?: ConvertType | AcceptType;
  crop?: boolean;
}

type IImagePickerHook = {
  result: ImagePickerResult;
  handlePickerImage: () => Promise<void>;
  remove: () => void;
};

const initialValue: ImagePickerResult = { file: null };
export function useImagePickerHandlers({
  maxSize,
  aspect,
  onChange,
  dialogTitle,
  acceptType,
  convertTo = AcceptType.JPEG,
  crop = true,
}: IImagePickerHookProps): IImagePickerHook {
  const bem = useBemCN('base-image-picker');
  const [result, setResult] = React.useState<ImagePickerResult>(initialValue);

  const handlePickerImage = React.useCallback(
    async function handlePickerImage() {
      const attachImage = await FileUtils.attachImage(maxSize, acceptType, convertTo);

      if (crop && (attachImage as RasterAttachImageResult).base64) {
        const rasterImage: RasterAttachImageResult = attachImage as RasterAttachImageResult;
        let dialogImageData: IImageCropData;
        let close: Function;

        const croppingData: IImageCropData = await UI.dialog(
          props => {
            close = props.close;
            return (
              <div {...bem('image-upload-dialog-content')}>
                <ImageCropper
                  image={rasterImage}
                  fallback={<div>No Image</div>}
                  aspect={aspect}
                  onChange={image => {
                    dialogImageData = image;
                  }}
                />
              </div>
            );
          },
          {
            title: dialogTitle,
            closable: true,
            buttons: (
              <div>
                <Button onClick={() => close(dialogImageData)}>Save</Button>
              </div>
            ),
          }
        );

        const croppedImage = await ImageUtils.getCroppedImage(
          croppingData.image,
          croppingData.croppedAreaPixels,
          croppingData.rotation,
          convertTo,
          attachImage.name
        );

        const value: ImagePickerResult = {
          raster: croppedImage,
          file: attachImage.file,
        };

        setResult(value);
        onChange?.(value);
      } else {
        if ((attachImage as RasterAttachImageResult).base64) {
          setResult({ file: attachImage.file, raster: (attachImage as RasterAttachImageResult).base64 });
          onChange?.({ file: attachImage.file, raster: (attachImage as RasterAttachImageResult).base64 });
        } else if ((attachImage as VectorAttachImageResult).vector) {
          setResult(attachImage as VectorAttachImageResult);
          onChange?.(attachImage as VectorAttachImageResult);
        } else if ((attachImage as AnimatedAttachImageResult).gif) {
          setResult(attachImage as AnimatedAttachImageResult);
          onChange?.(attachImage as AnimatedAttachImageResult);
        }
      }
    },
    [acceptType, aspect, bem, convertTo, crop, dialogTitle, maxSize, onChange]
  );

  const remove = React.useCallback(
    function remove() {
      setResult(initialValue);
      onChange?.(initialValue);
    },
    [onChange]
  );

  return {
    result,
    handlePickerImage,
    remove,
  };
}

export interface IImagePickerRef {
  remove: () => void;
}

export type IImagePickerProps = UIComponent &
  IImagePickerHookProps & {
    removeButtonText?: string;
    addButtonText?: string;
    noPreview?: boolean;
    value?: string;
  };

export const ImagePicker = React.forwardRef<IImagePickerRef, IImagePickerProps>(function ImagePicker(
  {
    className,
    dialogTitle = 'Crop Image',
    removeButtonText = 'Remove Photo',
    addButtonText = 'Choose Photo',
    maxSize = DefaultMaxImageAttachmentSize,
    value,
    noPreview = false,
    aspect,
    onChange = noop,
    crop = true,
    acceptType,
    convertTo = AcceptType.JPEG,
  }: IImagePickerProps,
  ref
) {
  const bem = bemBlock('image-picker');
  const {
    result: { file, raster, vector, gif },
    handlePickerImage,
    remove,
  } = useImagePickerHandlers({
    maxSize,
    aspect,
    onChange,
    crop,
  });

  React.useImperativeHandle(ref, () => ({
    remove,
  }));

  // TODO support for svg and gif,
  //  also return raw file

  return (
    <div className={bem(null, null, className)}>
      {!file && (
        <Button className={bem('button', [{ empty: true }])} onClick={handlePickerImage}>
          {addButtonText}
        </Button>
      )}
      {(raster || value) && (
        <>
          <Button className={bem('button', [{ selected: true }])} onClick={remove}>
            {removeButtonText}
          </Button>
          {!noPreview && (
            <>
              <br />
              {/* TODO use Image component */}
              <img src={raster ?? value} className={bem('image')} />
            </>
          )}
        </>
      )}
    </div>
  );
});

export const ThumbnailImagePicker = React.forwardRef<IImagePickerRef, IImagePickerProps>(function ImagePicker(
  {
    className,
    dialogTitle = 'Crop Image',
    removeButtonText = 'Remove Photo',
    addButtonText = 'Choose Photo',
    maxSize = 1024,
    value,
    noPreview = false,
    aspect,
    onChange = noop,
  }: IImagePickerProps,
  ref
) {
  const bem = useBemCN('thumbnail-image-picker');
  const {
    result: { file, raster, vector, gif },
    handlePickerImage,
    remove,
  } = useImagePickerHandlers({
    maxSize,
    aspect,
    onChange,
  });

  React.useImperativeHandle(ref, () => ({
    remove,
  }));

  // TODO support for svg and gif,
  //  also return raw file

  return (
    <div {...bem(null, null, className)}>
      <div
        {...bem('thumbnail-button')}
        style={
          raster || value
            ? {
                backgroundImage: `url('${raster ?? value}')`,
              }
            : null
        }
      >
        <Button
          variant={file || value ? 'transparent-black' : 'secondary'}
          circle
          onClick={() => void handlePickerImage()}
        >
          <SvgIcon Icon={PhotographIcon} />
        </Button>
      </div>
    </div>
  );
});
